import React from "react";
import { IconButton, Icon, Button, Collapse } from "@alm/flow-ds/components";
import NotificationIcon from "./NotificationIcon";
import { NotificationProps } from "records/Notification";
import { useTranslation } from "react-i18next";
import OverdueTaskList from "./OverdueTaskList";
import useShowMore from "hooks/useShowMore";
import { getDateTime } from "lib/dates";
import { useGetConvertedUserTime } from "hooks/useTimezone";

interface Props extends NotificationProps {
	handleDelete: () => Promise<void>;
	handleRead: () => Promise<void>;
}

const formatNotification = (msg: string) => {
	return msg.split("\n").map(str => (
		<>
			{str}
			<br />
		</>
	));
};

function Notification({
	read,
	msg,
	date,
	id,
	handleDelete,
	action,
	title,
	content,
	handleRead,
}: Props) {
	const { t, i18n } = useTranslation();

	// Truncate long notification messages
	const { maxLength, showMore, expanded } = useShowMore(msg.length, 100);

	const dateConverted = useGetConvertedUserTime(date);

	const isRead = read ? "read" : "unread";

	return (
		<li className="notifications__item">
			<Button
				onClick={handleRead}
				className={`notifications__item-icons notifications__item__readUnread notifications__item--${isRead}`}
				labelHidden
				label={
					read
						? t("main.labels.markedAsRead")
						: t("main.labels.markedAsUnread")
				}
				aria-pressed={read ? true : false}
				title={read ? t("main.labels.read") : t("main.labels.unread")}>
				<NotificationIcon
					action={action}
					isRead={read ? true : false}
				/>
			</Button>
			<div className="notifications__item-info">
				<div className={`notifications__item-info-title ${isRead}`}>
					{title}
				</div>
				<div className="notifications__item-info-menu">
					<IconButton
						onClick={handleDelete}
						className="btn-basic--dark btn-sm"
						icon={<Icon type="times" size="sm" weight="l" />}
						label={t("common.labels.delete")}
					/>
				</div>
				<div className={`notifications__item-info-desc ${isRead}`}>
					{msg.length > maxLength ? (
						<>
							{!expanded &&
								formatNotification(
									msg.substring(0, maxLength).trim() + "..."
								)}
							<Collapse
								id={id}
								open={expanded}
								className={expanded ? "d-inline" : ""}>
								{formatNotification(msg)}
							</Collapse>{" "}
							<Button
								aria-expanded={expanded}
								aria-controls={id}
								label={
									!expanded
										? t("main.labels.showMore")
										: t("main.labels.showLess")
								}
								onClick={showMore}
								className="notifications__item-info-desc-msg__more-less"
							/>
						</>
					) : (
						formatNotification(msg)
					)}
				</div>
				<div className={`notifications__item-info-date ${isRead}`}>
					{getDateTime(dateConverted, i18n.language, t, false)}
				</div>
				{action === "overdue" && content && (
					<OverdueTaskList content={content} id={id} />
				)}
			</div>
		</li>
	);
}

export default Notification;
